import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { formatSignupError, postCustomerSignup } from "@/lib/api";
import SiteBrandRow from "@/components/SiteBrandRow";
import AuthMobileTopBar from "@/components/auth/AuthMobileTopBar";
import BackendServiceNotice from "@/components/BackendServiceNotice";
import SignupLegalAgreement from "@/components/legal/SignupLegalAgreement";
import AuthFormFieldMessage from "@/components/auth/AuthFormFieldMessage";
import {
  REFERRAL_SOURCE_OPTIONS,
  getSignupFieldErrors,
  hasFieldErrors,
} from "@/lib/authFormValidation";
import { authInputClassName, useAuthFormValidation } from "@/hooks/useAuthFormValidation";

const SIGNUP_FIELDS = ["name", "email", "password", "referralSource"];

const SIGNUP_FIELD_TEST_IDS = {
  name: "signup-name",
  email: "signup-email",
  password: "signup-password",
  referralSource: "signup-referral-source",
};

function getFirstSignupFieldError(errors) {
  for (const key of SIGNUP_FIELDS) {
    if (errors[key]) return { key, message: errors[key] };
  }
  return null;
}

function focusSignupField(testId) {
  const el = document.querySelector(`[data-testid="${testId}"]`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  if (typeof el.focus === "function") {
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus();
    }
  }
}

export default function CustomerSignup() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    company: "",
    referralSource: "",
  });
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);
  const [legalTouched, setLegalTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const submitInFlight = useRef(false);
  const { touch, markSubmitted, shouldShow } = useAuthFormValidation(SIGNUP_FIELDS);

  const errors = useMemo(
    () =>
      getSignupFieldErrors({
        name: form.name,
        email: form.email,
        password: form.password,
        referralSource: form.referralSource,
      }),
    [form.name, form.email, form.password, form.referralSource],
  );

  const showLegalError = (legalTouched || shouldShow("referralSource")) && (!agreedTerms || !agreedPrivacy);

  const setField = (key) => (e) => {
    if (SIGNUP_FIELDS.includes(key)) touch(key);
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    markSubmitted();
    setLegalTouched(true);

    if (hasFieldErrors(errors)) {
      const firstError = getFirstSignupFieldError(errors);
      toast.error(firstError?.message || "입력 내용을 확인해 주세요.");
      if (firstError?.key) {
        focusSignupField(SIGNUP_FIELD_TEST_IDS[firstError.key]);
      }
      return;
    }
    if (!agreedTerms || !agreedPrivacy) {
      toast.error("이용약관 및 개인정보 처리방침에 동의해 주세요.");
      focusSignupField("signup-agree-terms");
      return;
    }
    if (/\.coom\b/i.test(form.email) && !window.confirm("이메일이 naver.coom 으로 입력되었습니다. naver.com 이 맞나요?")) {
      return;
    }
    if (submitInFlight.current || loading) return;

    submitInFlight.current = true;
    setLoading(true);
    try {
      const res = await postCustomerSignup({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        phone: form.phone.trim() || null,
        company: form.company.trim() || null,
        referral_source: form.referralSource,
        agreed_terms: agreedTerms,
        agreed_privacy: agreedPrivacy,
      });
      localStorage.setItem("dsite_token", res.data.access_token);
      localStorage.setItem("dsite_role", "customer");
      localStorage.setItem("dsite_user", JSON.stringify(res.data.user));
      toast.success("회원가입 완료");
      window.location.replace("/mypage");
    } catch (err) {
      toast.error(formatSignupError(err, "회원가입 중 오류가 발생했습니다."));
    } finally {
      submitInFlight.current = false;
      setLoading(false);
    }
  };

  const fieldMeta = [
    ["name", "이름 *", "text"],
    ["company", "회사명", "text"],
    ["phone", "연락처", "tel"],
    ["email", "이메일 *", "email"],
    ["password", "비밀번호 * (8자 이상, 영문+숫자)", "password"],
  ];

  return (
    <main data-testid="signup-page" className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6 py-16">
      <AuthMobileTopBar
        backTestId="signup-back-button"
        linkTestId="signup-logo-link"
        logoTestId="signup-logo-image"
      />
      <div className="absolute inset-0 bg-grid radial-mask opacity-50 pointer-events-none" />
      <div className="relative w-full max-w-md">
        <SiteBrandRow
          linkTo="/"
          size="lg"
          centered
          iconOnly
          className="mb-10 auth-page-brand-desktop"
          linkTestId="signup-logo-link-desktop"
          logoTestId="signup-logo-image-desktop"
        />
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-8">
          <BackendServiceNotice className="mb-5" compact />
          <h1 className="font-display text-2xl font-medium mb-2">회원가입</h1>
          <p className="text-sm text-white/50 mb-6">가입 후 마이페이지에서 견적·프로젝트를 관리하세요.</p>
          <form
            onSubmit={submit}
            className={`space-y-3${loading ? " pointer-events-none" : ""}`}
            noValidate
            aria-busy={loading ? "true" : undefined}
          >
            {fieldMeta.map(([key, label, type]) => {
              const validated = SIGNUP_FIELDS.includes(key);
              const showError = validated && shouldShow(key) && errors[key];
              return (
                <div key={key}>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1.5 block">
                    {label}
                  </label>
                  <input
                    type={type}
                    value={form[key]}
                    onChange={setField(key)}
                    onBlur={() => validated && touch(key)}
                    autoComplete={key === "password" ? "new-password" : key}
                    data-testid={`signup-${key}`}
                    aria-invalid={showError ? "true" : undefined}
                    className={validated ? authInputClassName(showError) : authInputClassName(false)}
                    placeholder={
                      key === "name"
                        ? "이름을 입력하세요"
                        : key === "email"
                          ? "example@email.com"
                          : key === "password"
                            ? "비밀번호를 입력하세요"
                            : undefined
                    }
                  />
                  {validated ? (
                    <AuthFormFieldMessage message={shouldShow(key) ? errors[key] : ""} />
                  ) : null}
                </div>
              );
            })}

            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1.5 block">
                어떤 경로로 오게 되었나요? *
              </label>
              <select
                value={form.referralSource}
                onChange={setField("referralSource")}
                onBlur={() => touch("referralSource")}
                data-testid="signup-referral-source"
                aria-invalid={shouldShow("referralSource") && errors.referralSource ? "true" : undefined}
                className={`${authInputClassName(shouldShow("referralSource") && errors.referralSource)} text-white`}
              >
                {REFERRAL_SOURCE_OPTIONS.map((opt) => (
                  <option key={opt.value || "empty"} value={opt.value} className="bg-[#0a0a0a]">
                    {opt.label}
                  </option>
                ))}
              </select>
              <AuthFormFieldMessage
                message={shouldShow("referralSource") ? errors.referralSource : ""}
              />
            </div>

            <SignupLegalAgreement
              agreedTerms={agreedTerms}
              agreedPrivacy={agreedPrivacy}
              onChangeTerms={(value) => {
                setLegalTouched(true);
                setAgreedTerms(value);
              }}
              onChangePrivacy={(value) => {
                setLegalTouched(true);
                setAgreedPrivacy(value);
              }}
            />
            <AuthFormFieldMessage
              message={showLegalError ? "이용약관 및 개인정보 처리방침에 동의해 주세요." : ""}
            />

            <button disabled={loading} data-testid="signup-submit" className="btn-primary w-full justify-center mt-4 disabled:opacity-50">
              {loading ? "가입 중..." : "회원가입"}
            </button>
          </form>
          <p className="text-xs text-white/40 mt-5 text-center">
            이미 계정이 있나요? <Link to="/login" className="text-white underline">로그인</Link>
          </p>
        </div>
      </div>
    </main>
  );
}
